import React from "react";
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  Chip,
  Box,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Report } from "../types/report.ts";
import { formatDate } from "../utils/helpers.ts";
import EditIcon from '@mui/icons-material/Edit';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import DeleteIcon from '@mui/icons-material/Delete';

const ReportCard: React.FC<{ report: Report }> = ({ report }) => {
  const navigate = useNavigate();

  // Check if validFrom is less than 24 hours ago
  const isWithin24Hours = () => {
    const validFromDate = new Date(report.createdAt);
    const now = new Date();
    const hoursDiff = (now.getTime() - validFromDate.getTime()) / (1000 * 60 * 60);
    return hoursDiff < 24;
  };

  const isOwner = (report?.createdBy?.id == localStorage.getItem('uid')) 
    || (localStorage.getItem('uid') == 1) 
    || (localStorage.getItem('uid') == 3);
  // either owner or admin. ugly but who cares..
  const showDeleteButton = isOwner && isWithin24Hours();

  return (
    <Card
      sx={{
        minHeight: 240,
        display: "flex",
        flexDirection: "column",
        cursor: "pointer",
        background: 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(10px)',
        borderRadius: '16px',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
        transition: 'all 0.3s ease',
        border: '1px solid rgba(255, 255, 255, 0.5)',
        '&:hover': {
          transform: 'translateY(-8px)',
          boxShadow: '0 16px 48px rgba(0, 0, 0, 0.25)',
        },
      }}
      onClick={() =>
        navigate(
          report.publishedAt
            ? `/report/${report.documentId}`
            : `/report/create/${report.documentId}`
        )
      }
    >
      <CardContent sx={{ flexGrow: 1, p: 3 }}>
        <Box sx={{ mb: 2 }}>
          {(report.status !== "published") && (
            <Chip 
              label="ENTWURF" 
              size="small"
              sx={{ 
                background: 'linear-gradient(135deg, #ffa726 0%, #fb8c00 100%)',
                color: 'white',
                fontWeight: 600,
                fontSize: '11px',
                mb: 1.5,
                boxShadow: '0 2px 8px rgba(255, 167, 38, 0.3)'
              }}
            />
          )}
          {(report.status === "published") && (
            <Chip 
              label="VERÖFFENTLICHT" 
              size="small"
              sx={{ 
                background: 'linear-gradient(135deg, #66bb6a 0%, #43a047 100%)',
                color: 'white',
                fontWeight: 600,
                fontSize: '11px',
                mb: 1.5,
                boxShadow: '0 2px 8px rgba(102, 187, 106, 0.3)'
              }}
            />
          )}
        </Box>
        <Typography
          gutterBottom
          variant="h6"
          sx={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            lineHeight: 1.3,
            maxHeight: "2.6em",
            fontWeight: 700,
            color: '#333',
            mb: 2
          }}
        >
          {report.title}
        </Typography>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          gap: 0.5,
          color: '#666'
        }}>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            📅 Gültig ab: {formatDate(report.valid_from)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            🕒 Von {report.createdBy.firstname} {report.createdBy.lastname} am {formatDate(report.createdAt)} 
          </Typography>
          <Typography variant="caption" color="text.secondary">
            
          </Typography>
        </Box>
      </CardContent>
      <CardActions sx={{ 
        justifyContent: 'center', 
        gap: 1.5,
        p: 2,
        pt: 0,
        flexWrap: 'wrap'
      }}>
        {isOwner && (
        <Button
          size="small"
          startIcon={<EditIcon />}
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/report/create/${report.documentId}`);
          }}
          sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            fontWeight: 600,
            px: 2.5,
            py: 0.75,
            borderRadius: '8px',
            textTransform: 'none',
            fontSize: '13px',
            boxShadow: '0 2px 8px rgba(102, 126, 234, 0.3)',
            '&:hover': {
              background: 'linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%)',
              boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)',
            }
          }}
        >
          Bearbeiten
        </Button>
        )}
        {(report.status === "published") && (
          <Button
            size="small"
            startIcon={<OpenInNewIcon />}
            onClick={(e) => {
              e.stopPropagation();
              const date = new Date(report.valid_from);
              date.setDate(date.getDate() + 1);
              const formattedDate = date.toISOString().slice(0, 10);
              window.open(
                `https://schnee.report/snowreport?date=${formattedDate}&anchor=${report.documentId}`,
                "_blank"
              );
            }}
            sx={{
              background: 'linear-gradient(135deg, #26a69a 0%, #00897b 100%)',
              color: 'white',
              fontWeight: 600,
              px: 2.5,
              py: 0.75,
              borderRadius: '8px',
              textTransform: 'none',
              fontSize: '13px',
              boxShadow: '0 2px 8px rgba(38, 166, 154, 0.3)',
              '&:hover': {
                background: 'linear-gradient(135deg, #00897b 0%, #00695c 100%)',
                boxShadow: '0 4px 12px rgba(38, 166, 154, 0.4)',
              }
            }}
          >
            Öffnen
          </Button>
        )}
        {showDeleteButton && (
          <Button
            size="small"
            startIcon={<DeleteIcon />}
            onClick={async (e) => {
              e.stopPropagation();

              const confirmed = window.confirm(
                "Möchten Sie diesen Bericht wirklich löschen?"
              );

              if (confirmed) {
                try {
                  const token = localStorage.getItem("jwt");
                  const headers = {
                    ...(token && { Authorization: `Bearer ${token}` }),
                  };

                  await axios.delete(
                    `https://backend.schnee.report/content-manager/collection-types/api::report.report/${report.documentId}?locale=*`,
                    { headers }
                  );

                  navigate("/dashboard");
                  window.location.reload();
                } catch (error) {
                  console.error("Fehler beim Löschen des Berichts:", error);
                  alert(
                    "Fehler beim Löschen des Berichts. Bitte versuchen Sie es erneut."
                  );
                }
              }
            }}
            sx={{
              background: 'linear-gradient(135deg, #ef5350 0%, #e53935 100%)',
              color: 'white',
              fontWeight: 600,
              px: 2.5,
              py: 0.75,
              borderRadius: '8px',
              textTransform: 'none',
              fontSize: '13px',
              boxShadow: '0 2px 8px rgba(239, 83, 80, 0.3)',
              '&:hover': {
                background: 'linear-gradient(135deg, #e53935 0%, #c62828 100%)',
                boxShadow: '0 4px 12px rgba(239, 83, 80, 0.4)',
              }
            }}
          >
            Löschen
          </Button>
        )}
      </CardActions>
    </Card>
  );
};

export default ReportCard;
